
import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function PropertySearchPage() {
  const [filters, setFilters] = useState({
    location: '',
    priceMin: '',
    priceMax: '',
    bedrooms: '',
    bathrooms: '',
    propertyType: '',
    sqftMin: '',
    sqftMax: ''
  });

  const [savedProperties, setSavedProperties] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleSaveProperty = (id: number) => {
    setSavedProperties(prev => 
      prev.includes(id) 
        ? prev.filter(propId => propId !== id)
        : [...prev, id]
    );
  };

  const handleViewDetails = (id: number) => {
    window.location.href = `/property-detail/${id}`;
  };

  const properties = [
    {
      id: 1,
      address: '1234 Maple Street, Royal Oak, MI 48067',
      price: 425000,
      bedrooms: 3,
      bathrooms: 2,
      sqft: 1850,
      lotSize: '0.25 acres',
      yearBuilt: 2018,
      propertyType: 'Single Family',
      status: 'Active',
      daysOnMarket: 12,
      image: 'https://public.readdy.ai/ai/img_res/b40f82b7891570135e8cef1ceb61a33f.jpg',
      features: ['Updated Kitchen', 'Hardwood Floors', 'Finished Basement', 'Garage']
    },
    {
      id: 2,
      address: '5678 Oak Avenue, Plymouth, MI 48170',
      price: 365000,
      bedrooms: 4,
      bathrooms: 2.5,
      sqft: 2100,
      lotSize: '0.30 acres',
      yearBuilt: 2015,
      propertyType: 'Single Family',
      status: 'Active',
      daysOnMarket: 8,
      image: 'https://readdy.ai/api/search-image?query=Charming%20colonial%20style%20home%20with%20front%20porch%2C%20Plymouth%20Michigan%20neighborhood%2C%20family-friendly%20residential%20area&width=400&height=300&seq=property-2&orientation=landscape',
      features: ['Master Suite', 'Open Floor Plan', 'Deck', 'Two Car Garage']
    },
    {
      id: 3,
      address: '9012 Pine Court, Rochester Hills, MI 48309',
      price: 485000,
      bedrooms: 4,
      bathrooms: 3,
      sqft: 2450,
      lotSize: '0.40 acres',
      yearBuilt: 2020,
      propertyType: 'Single Family',
      status: 'Active',
      daysOnMarket: 5,
      image: 'https://readdy.ai/api/search-image?query=Luxury%20modern%20home%20with%20stone%20and%20brick%20exterior%2C%20Rochester%20Hills%20Michigan%20upscale%20neighborhood%2C%20professional%20real%20estate%20photo&width=400&height=300&seq=property-3&orientation=landscape',
      features: ['Gourmet Kitchen', 'Walk-in Closets', 'Three Car Garage', 'Premium Lot']
    },
    {
      id: 4,
      address: '3456 Elm Drive, Shelby Township, MI 48315',
      price: 285000,
      bedrooms: 3,
      bathrooms: 2,
      sqft: 1650,
      lotSize: '0.20 acres',
      yearBuilt: 2019,
      propertyType: 'Single Family',
      status: 'Active',
      daysOnMarket: 15,
      image: 'https://readdy.ai/api/search-image?query=New%20construction%20home%20in%20Shelby%20Township%20Michigan%2C%20modern%20suburban%20development%2C%20move-in%20ready%20family%20home&width=400&height=300&seq=property-4&orientation=landscape',
      features: ['New Construction', 'Energy Efficient', 'Open Concept', 'Covered Patio']
    },
    {
      id: 5,
      address: '7890 Cedar Lane, Farmington Hills, MI 48334',
      price: 395000,
      bedrooms: 3,
      bathrooms: 2.5,
      sqft: 1950,
      lotSize: '0.28 acres',
      yearBuilt: 2017,
      propertyType: 'Single Family',
      status: 'Active',
      daysOnMarket: 22,
      image: 'https://readdy.ai/api/search-image?query=Contemporary%20home%20with%20large%20windows%20and%20modern%20design%2C%20Farmington%20Hills%20Michigan%2C%20well-maintained%20landscaping&width=400&height=300&seq=property-5&orientation=landscape',
      features: ['Vaulted Ceilings', 'Fireplace', 'Updated Bathrooms', 'Fenced Yard']
    },
    {
      id: 6,
      address: '2468 Birch Street, Troy, MI 48084',
      price: 445000,
      bedrooms: 4,
      bathrooms: 2.5,
      sqft: 2200,
      lotSize: '0.35 acres',
      yearBuilt: 2016,
      propertyType: 'Single Family',
      status: 'Active',
      daysOnMarket: 18,
      image: 'https://readdy.ai/api/search-image?query=Elegant%20two-story%20home%20with%20brick%20exterior%2C%20Troy%20Michigan%20prestigious%20neighborhood%2C%20mature%20trees%20and%20landscaping&width=400&height=300&seq=property-6&orientation=landscape',
      features: ['Formal Dining', 'Study/Office', 'Mudroom', 'Sprinkler System']
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              Property Search
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Discover your dream home in Metro Detroit. Browse current listings with detailed information, photos, and neighborhood insights.
            </p>
          </div>

          {/* Search Filters */}
          <div className="bg-white rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-black mb-4">Search Filters</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-black mb-1">Location</label>
                <input
                  type="text"
                  placeholder="City, ZIP, or Neighborhood"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Property Type</label>
                <select
                  value={filters.propertyType}
                  onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                >
                  <option value="">All Types</option>
                  <option value="Single Family">Single Family</option>
                  <option value="Condo">Condo</option>
                  <option value="Townhouse">Townhouse</option>
                  <option value="Multi-Family">Multi-Family</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Bedrooms</label>
                <select
                  value={filters.bedrooms}
                  onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                >
                  <option value="">Any</option>
                  <option value="1">1+</option>
                  <option value="2">2+</option>
                  <option value="3">3+</option>
                  <option value="4">4+</option>
                  <option value="5">5+</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Bathrooms</label>
                <select
                  value={filters.bathrooms}
                  onChange={(e) => handleFilterChange('bathrooms', e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                >
                  <option value="">Any</option>
                  <option value="1">1+</option>
                  <option value="1.5">1.5+</option>
                  <option value="2">2+</option>
                  <option value="2.5">2.5+</option>
                  <option value="3">3+</option>
                </select>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-black mb-1">Min Price</label>
                <input
                  type="number"
                  placeholder="$0"
                  value={filters.priceMin}
                  onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Max Price</label>
                <input
                  type="number"
                  placeholder="No Max"
                  value={filters.priceMax}
                  onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Min Sq Ft</label>
                <input
                  type="number"
                  placeholder="Any"
                  value={filters.sqftMin}
                  onChange={(e) => handleFilterChange('sqftMin', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-black mb-1">Max Sq Ft</label>
                <input
                  type="number"
                  placeholder="Any"
                  value={filters.sqftMax}
                  onChange={(e) => handleFilterChange('sqftMax', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Results Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="text-white">
              <span className="text-lg font-semibold">{properties.length} Properties Found</span>
              <span className="text-gray-400 ml-2">in Metro Detroit</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex bg-white rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1 rounded cursor-pointer transition-colors ${
                    viewMode === 'grid' ? 'bg-yellow-500 text-black' : 'text-gray-600 hover:text-black'
                  }`}
                >
                  <i className="ri-grid-line"></i>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 rounded cursor-pointer transition-colors ${
                    viewMode === 'list' ? 'bg-yellow-500 text-black' : 'text-gray-600 hover:text-black'
                  }`}
                >
                  <i className="ri-list-unordered"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Property Listings */}
          <div className={`grid gap-6 ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
            {properties.map((property) => (
              <div key={property.id} className="bg-white rounded-lg overflow-hidden border-2 border-yellow-500 hover:border-yellow-400 transition-colors">
                <div className="relative">
                  <img
                    src={property.image}
                    alt={property.address}
                    className="w-full h-48 object-cover object-top"
                  />
                  <button
                    onClick={() => toggleSaveProperty(property.id)}
                    className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                      savedProperties.includes(property.id)
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white text-gray-600 hover:text-black'
                    }`}
                  >
                    <i className={savedProperties.includes(property.id) ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  </button>
                  <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded text-xs font-medium">
                    {property.status}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-2xl font-bold text-yellow-600">
                      ${property.price.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500">
                      {property.daysOnMarket} days on market
                    </div>
                  </div>
                  
                  <div className="text-gray-600 text-sm mb-3">{property.address}</div>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                    <span><i className="ri-hotel-bed-line mr-1"></i>{property.bedrooms} bed</span>
                    <span><i className="ri-drop-line mr-1"></i>{property.bathrooms} bath</span>
                    <span><i className="ri-ruler-line mr-1"></i>{property.sqft.toLocaleString()} sqft</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {property.features.slice(0, 3).map((feature, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {feature}
                      </span>
                    ))}
                    {property.features.length > 3 && (
                      <span className="text-gray-500 text-xs">+{property.features.length - 3} more</span>
                    )}
                  </div>
                  
                  <div className="w-full">
                    <button 
                      onClick={() => handleViewDetails(property.id)}
                      className="w-full bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-information-line mr-2"></i>
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Saved Properties */}
          {savedProperties.length > 0 && (
            <div className="mt-12 bg-white rounded-lg p-6">
              <h3 className="text-xl font-bold text-black mb-4">
                <i className="ri-heart-fill text-yellow-500 mr-2"></i>
                Saved Properties ({savedProperties.length})
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {properties
                  .filter(property => savedProperties.includes(property.id))
                  .map((property) => (
                    <div key={property.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="text-lg font-bold text-yellow-600 mb-1">
                        ${property.price.toLocaleString()}
                      </div>
                      <div className="text-gray-600 text-sm mb-2">{property.address}</div>
                      <div className="text-xs text-gray-500">
                        {property.bedrooms} bed • {property.bathrooms} bath • {property.sqft.toLocaleString()} sqft
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-yellow-500 text-black rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Need Help Finding the Perfect Home?</h3>
              <p className="text-black mb-6">
                Let me help you navigate the market and find properties that match your specific needs and budget. Get access to off-market listings and expert guidance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-search-line mr-2"></i>
                  Start Personal Search
                </button>
                <button className="bg-transparent border-2 border-black text-black px-8 py-3 rounded-lg hover:bg-black hover:text-white transition-colors font-semibold cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call: (248) 248-4801
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}